import { FUNCTIONS, LANGUAGES, REFERENCE_LANGUAGE, format, type Arg, type Message } from "@eledone/i18n";
import { FluentBundle, FluentResource } from "@fluent/bundle";
import { useLocalStore } from "@/hooks/use-local-store";
import type { LocalState } from "@/lib/types";

export type Language = (typeof LANGUAGES)[number];
export type Args = Record<string, Arg>;
export type { Message };
export type T = {
  (id: string, args?: Args): string;
  (message: Message): string;
};

type Catalogs = { keys(): string[]; (key: string): string };
declare const require: { context(directory: string, deep: boolean, filter: RegExp): Catalogs };

// The Backend's codes come from @eledone/i18n, the pane's own texts from locales/.
const SOURCES: Record<Language, Catalogs[]> = {
  fr: [
    require.context("@eledone/i18n-locales/fr", false, /\.ftl$/),
    require.context("../../../locales/fr", false, /\.ftl$/),
  ],
  en: [
    require.context("@eledone/i18n-locales/en", false, /\.ftl$/),
    require.context("../../../locales/en", false, /\.ftl$/),
  ],
};

const LOCALE: Record<Language, string> = { fr: "fr-FR", en: "en-GB" };

function create(language: Language) {
  const locale = LOCALE[language];
  const bundle = new FluentBundle(locale, { useIsolating: false, functions: FUNCTIONS });
  for (const catalogs of SOURCES[language]) {
    for (const key of catalogs.keys().filter((k) => k.startsWith("./"))) {
      const errors = bundle.addResource(new FluentResource(catalogs(key)));
      if (errors.length) console.error("[i18n]", key, errors);
    }
  }

  const t = ((target: string | Message, args?: Args) =>
    format(bundle, typeof target === "string" ? { id: target, args } : target)) as T;
  const has = (id: string) => bundle.hasMessage(id);

  return { language, locale, t, has };
}

export type I18n = ReturnType<typeof create>;

const isLanguage = (value: unknown): value is Language => LANGUAGES.indexOf(value as Language) >= 0;

export function pickLanguage(chosen?: string | null, organization?: string | null): Language {
  const office = typeof Office === "undefined" ? undefined : Office.context?.displayLanguage;
  return [chosen, office?.slice(0, 2).toLowerCase(), organization].filter(isLanguage)[0] ?? REFERENCE_LANGUAGE;
}

const made: Partial<Record<Language, I18n>> = {};
const i18nFor = (language: Language) => (made[language] ??= create(language));
const languageOf = (state: LocalState) =>
  pickLanguage(state.userProfile?.language, state.userProfile?.organizationLanguage);

export const getI18n = () => i18nFor(languageOf(useLocalStore.getState()));
export const useI18n = () => i18nFor(useLocalStore(languageOf));
export const useT = () => useI18n().t;
